import type { CardResult, Difficulty, Kind, SignId, TeamId } from '../shared/index.js';

export const s = {
  name: 'Sessiz Sinema',
  pitch: 'Tek kelime etmeden filmi, diziyi, şarkıyı takımına anlat',
  team: { a: 'Yeşil takım', b: 'Mor takım' } satisfies Record<TeamId, string>,
  kind: { film: 'Film', dizi: 'Dizi', kitap: 'Kitap', sarki: 'Şarkı', deyim: 'Atasözü ya da deyim', ifade: 'İfade' } satisfies Record<Kind, string>,
  kindGesture: {
    film: 'Eski kamera kolu çevir',
    dizi: 'Havaya ekran çiz',
    kitap: 'Ellerini kitap gibi aç',
    sarki: 'Elini ağzına götürüp şarkı söyler gibi yap',
    deyim: 'İki elinle tırnak işareti yap',
    ifade: 'Elini ağzının önünde açıp kapat, konuşur gibi yap',
  } satisfies Record<Kind, string>,
  words: (n: number) => `${n} kelime`,
  settings: {
    teams: 'Takımlar',
    teamsHint: 'Bir oyuncuya dokunarak takımını değiştir. Takımı seçilmeyenler başlarken dağıtılır.',
    unassigned: 'Takımsız',
    shuffle: 'Karıştır',
    empty: 'Henüz kimse yok',
    categories: 'Türler',
    adultHint: '+18 kategoriler açık cinsel içerik içerir; yalnızca yetişkinler içindir.',
    difficulty: 'Zorluk',
    difficultyName: { kolay: 'Kolay', karisik: 'Karışık', zor: 'Zor' } satisfies Record<Difficulty, string>,
    difficultyHint: {
      kolay: 'Herkesin bildiği başlıklar.',
      karisik: 'Kolay ve zor başlıklar karışık gelir.',
      zor: 'Daha az bilinen, uzun başlıklar.',
    } satisfies Record<Difficulty, string>,
    seconds: 'Tur süresi',
    sec: (n: number) => `${n} sn`,
    passes: 'Pas hakkı',
    unlimited: 'Sınırsız',
    turns: 'Takım başına anlatma',
    turnsHint: (n: number) => (n === 0 ? 'Oda sahibi bitirene kadar sürer.' : `Toplam ${n * 2} tur.`),
    penalty: 'Konuşma cezası',
    penaltyHint: 'Rakip takım “Konuştu!” derse kart geçer; ceza açıksa anlatan takımdan 1 puan da düşer.',
    on: 'Açık',
    off: 'Kapalı',
    readOnly: 'Ayarları oda sahibi seçer.',
    addTitle: 'Başlık ekle',
    addTitleHint: 'Eklediğin başlık “Arkadaş başlıkları” türüne girer ve kimseye listelenmez.',
    titleLabel: 'Film, dizi, kitap, şarkı, deyim ya da ifade',
    kindLabel: 'Türü',
    save: 'Başlığı ekle',
    saving: 'Ekleniyor…',
    saved: (t: string) => `“${t}” eklendi.`,
    needTwo: 'Her takımda en az 2 kişi olmalı.',
  },
  play: {
    turn: (n: number, total: number | null) => (total ? `Tur ${Math.min(n + 1, total)}/${total}` : `Tur ${n + 1}`),
    you: 'sen',
    narrating: 'anlatıyor',
    next: 'Sıradaki anlatıcı',
    startMine: 'Anlatmaya başla',
    startMineHint: 'Başlayınca başlığı yalnızca sen ve rakip takım göreceksiniz. Konuşmak yok.',
    startForThem: (nick: string) => `${nick} yerine başlat`,
    skipNarrator: 'Anlatıcıyı atla',
    waitNarrator: (nick: string) => `${nick} turu başlatınca süre işlemeye başlayacak.`,
    correct: 'Doğru',
    pass: (left: number | null) => (left === null ? 'Pas' : `Pas (${left})`),
    undo: 'Geri al',
    foul: 'Konuştu!',
    foulHint: (penalty: boolean) =>
      penalty ? 'Anlatıcı ses çıkarır ya da dudaklarıyla söylerse bas: kart geçer, takımı 1 puan kaybeder.' : 'Anlatıcı ses çıkarır ya da dudaklarıyla söylerse bas: kart geçer.',
    watchHint: 'Başlığı sen de görüyorsun. Sakın söyleme.',
    guess: (nick: string) => `${nick} anlatıyor, tahmin et!`,
    guessHint: 'Başlığı göremezsin. İşaretleri izle, tahminini yüksek sesle söyle.',
    guessCount: 'Bu turda bilinen',
    headline: {
      narrator: 'Sen anlatıyorsun · konuşmak yok!',
      watcher: 'Başlığı sen de görüyorsun. Sakın söyleme.',
    },
    gesture: (g: string) => `İşaret: ${g.charAt(0).toLocaleLowerCase('tr')}${g.slice(1)}`,
    signsLabel: 'Klasik işaretler',
    signsShort: {
      tur: 'Tür',
      kelime: 'Kelime sayısı: parmak',
      hece: 'Hece: kola dokun',
      benzer: 'Benzer ses: kulak',
      bildin: 'Bildin: buruna dokun',
    } satisfies Record<SignId, string>,
    signsTap: 'İşarete dokun, takımın ekranında görsün.',
    signShown: (sign: SignId, kind: string | null, words: number | null) =>
      ({
        tur: kind ? `Tür: ${kind}` : 'Tür',
        kelime: words ? `${words} kelime` : 'Kelime sayısı',
        hece: 'Heceye bölüyor',
        benzer: 'Benzer ses',
        bildin: 'Bildin!',
      })[sign],
    spectator: 'Bu tur izliyorsun.',
    narratorNote: 'Takımın başlığın tamamını söyleyince “Doğru”ya bas.',
    stats: { correct: 'Doğru', pass: 'Pas', foul: 'Konuştu' } satisfies Record<CardResult, string>,
    flash: { correct: 'Doğru! +1', pass: 'Pas', foul: 'Konuştu!' } satisfies Record<CardResult, string>,
    lastTurn: (team: string, pts: number) => `${team} bu turda ${pts > 0 ? '+' : ''}${pts}`,
    noCards: 'Bu turda kart geçmedi.',
    wordN: (i: number) => `${i}. kelime`,
  },
  podium: {
    title: 'Oyun bitti',
    won: 'kazandı!',
    draw: 'Berabere',
    back: 'Lobiye dönülüyor…',
  },
  howto: {
    title: 'Nasıl oynanır',
    steps: [
      ['Takımlar', 'İki takım sırayla anlatır. Her turda anlatan takımdan bir kişi anlatıcı olur.'],
      ['Anlat', 'Anlatıcı başlığı, türünü ve kelime sayısını görür. Konuşmadan, ses çıkarmadan, yalnızca hareketle anlatır.'],
      ['Kontrol', 'Rakip takım da başlığı görür. Anlatıcı konuşursa “Konuştu!”ya basar; kart geçer.'],
      ['Puan', 'Her doğru +1. Konuşma cezası açıksa her “Konuştu!” −1. Pas hakkı ayarlardan seçilir.'],
    ] as [string, string][],
    signsTitle: 'Klasik işaretler',
    signs: [
      ['Tür', 'Önce türü göster: film için kamera kolu çevir, dizi için ekran çiz, kitap için ellerini aç, şarkı için şarkı söyler gibi yap, deyim için tırnak işareti, ifade için elini ağzının önünde açıp kapat.'],
      ['Kelime sayısı', 'Kaç kelime olduğunu parmaklarınla göster.'],
      ['Kaçıncı kelime', 'Hangi kelimeyi anlattığını yine parmakla göster; sıra karışmasın.'],
      ['Hece', 'Kelimeyi heceye bölmek için parmaklarını ön koluna koy: kaç parmak, o kadar hece.'],
      ['Bütün başlık', 'Kollarınla büyük bir daire çiz: başlığın tamamını bir kerede anlatacaksın.'],
      ['Benzer ses', 'Kulağını çek: “buna benzer bir kelime” demek.'],
      ['Bildin', 'Burnuna dokunup tahmin edeni göster: “tam olarak bu”.'],
    ] as [string, string][],
  },
};
